import { useCallback } from 'react';
import { useCurrentWallet, useDisconnectWallet as useSdkDisconnectWallet } from '@mysten/dapp-kit';

import { useWalletStore } from './useWalletStore';
import { useWalletConnectionResolver } from './useWalletConnectionResolver';
import { WALLET_CONNECTION_ORDER, hasResolvableConnection } from '../utils/wallet.util';
import { useSuiWallet } from './useSuiWallet';
import { useZkLoginWallet } from './useZkLoginWallet';
import type { WalletSliceKey } from '../types/wallet-slice.type';
import { isEnokiWallet } from '@mysten/enoki';

type DisconnectWalletOptions = {
	fallbackToSui?: boolean;
};

export function useDisconnectWallets() {
	const { mutateAsync: disconnectSdkWallet, isPending, isSuccess, reset } = useSdkDisconnectWallet();
	const setWalletDisconnected = useWalletStore((state) => state.setWalletDisconnected);
	const resolveWalletConnection = useWalletConnectionResolver();
	const { currentWallet } = useCurrentWallet();
	const suiWalletSlice = useSuiWallet();
	const zkLoginWalletSlice = useZkLoginWallet();
	const WALLET_DISCONNECTION_ORDER = currentWallet
		? isEnokiWallet(currentWallet)
			? [...WALLET_CONNECTION_ORDER].reverse()
			: WALLET_CONNECTION_ORDER
		: WALLET_CONNECTION_ORDER;

	const disconnectWallet = useCallback(
		async (walletType: WalletSliceKey, options?: DisconnectWalletOptions) => {
			const slice = walletType === 'suiWallet' ? suiWalletSlice : zkLoginWalletSlice;
			if (!hasResolvableConnection(slice)) {
				return false;
			}

			try {
				await resolveWalletConnection(walletType);
				await disconnectSdkWallet();
			} catch {
				// Ignore errors during disconnect. It might happen if the wallet is already disconnected or zkLogin session expired.
			} finally {
				reset();
				setWalletDisconnected(walletType);
			}

			const shouldFallbackToSui = options?.fallbackToSui ?? walletType === 'zkLoginWallet';
			if (shouldFallbackToSui && walletType === 'zkLoginWallet' && hasResolvableConnection(suiWalletSlice)) {
				try {
					await resolveWalletConnection('suiWallet');
				} catch (error) {
					console.warn('Failed to re-connect Sui wallet after disconnecting zkLogin wallet', error);
				}
			}

			return true;
		},
		[disconnectSdkWallet, resolveWalletConnection, reset, setWalletDisconnected, suiWalletSlice, zkLoginWalletSlice],
	);

	const disconnectAll = useCallback(async () => {
		for (const walletType of WALLET_DISCONNECTION_ORDER) {
			await disconnectWallet(walletType, { fallbackToSui: false });
		}
	}, [WALLET_DISCONNECTION_ORDER, disconnectWallet]);

	return {
		disconnectWallet,
		disconnectAll,
		isPending,
		isSuccess,
	};
}
